import { Component, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { CFSConnectionModalComponent } from "@app-cmc/core/components/layout";
import { ProfileModel } from "@app-cmc/core/models";
import { AccountService } from "@app-cmc/core/services/account.service";
import { AppConfigService } from "@app-cmc/core/services/app-config.service";
import { AuthService } from "@app-cmc/core/services/auth.service";
import { RoleTypes, Subscription } from "@app-cmc/models";
import { CashRegistersService, CFSConnectionService, CompanyDataService, LocationService } from "@app-cmc/services";
import { ToastService } from "@app-cmc/shared";
import { NgbDropdown, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { CFSConnectionModalResult, CFSConnectionSettings, ClientCashRegister } from "cfs-communication-pack";
import { ConfigurationService, NavigateHomeFeatureSettingsDto, PermissionTypeDto } from "clearline-api";
import { UserPermissionService } from "clearline-common";
import { alertApiError, IApplicationConfig } from "core-kit";
import { BehaviorSubject, combineLatest, Observable, Subject } from "rxjs";
import { delay, filter, map, take, takeUntil } from "rxjs/operators";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"]
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild("dropdownRef", { static: false, read: NgbDropdown }) ngbDropdown: NgbDropdown;

  readonly mobileScreenWidth = 576;
  readonly brandLogo$ = new BehaviorSubject<string>("");
  readonly navigateHomeFeature$: Observable<NavigateHomeFeatureSettingsDto> = this.configurationService
    .getContext()
    .pipe(map((context) => context.featureSettings.navigateHome));

  protected subscription$ = new BehaviorSubject<Subscription>(new Subscription());
  protected loginOrLandingOnly = false;
  protected user: ProfileModel | null = null;
  protected windowWidth = 0;
  protected isMinLocationManager = false;
  protected isMobileApp = false;
  protected adminPortalUrl = "";
  protected periodEndDate = "";
  protected periodEndDays = 0;
  protected hasCfsTerminals = false;
  protected hasLogoutButton = false;

  private readonly auditTimeDelay = 300;
  private unsubscribe$ = new Subject<void>();
  private profileUrl = "";

  @HostListener("window:resize") handleResizeEvent() {
    this.setWindowWidth();
  }

  constructor(
    private route: Router,
    private configurationService: ConfigurationService,
    private accountService: AccountService,
    private authService: AuthService,
    private companyDataService: CompanyDataService,
    private modalSvc: NgbModal,
    private cfsConnectionService: CFSConnectionService,
    private configSvc: AppConfigService,
    private locationService: LocationService,
    private cashRegistersService: CashRegistersService,
    private permissionService: UserPermissionService,
    private toaster: ToastService
  ) {}

  ngOnInit() {
    this.setConfigData();
    this.setIsMobileApp();
    this.setWindowWidth();
    this.setCurrentCashRegister();
    this.onEvents();
  }

  toggleDropdown(): void {
    const isItOpenAction: boolean = this.ngbDropdown?.isOpen();

    if (isItOpenAction) {
      this.companyDataService.retrieveData(true);
    }
  }

  logout() {
    this.authService.logout();
  }

  customerScreen(): void {
    const modal = this.modalSvc.open(CFSConnectionModalComponent, {
      backdrop: "static",
      size: "lg",
      centered: true,
      windowClass: "custom"
    });
    const currentCashRegister: ClientCashRegister = this.cashRegistersService.getCurrentCashRegister();
    const modalComponent: CFSConnectionModalComponent = modal.componentInstance;
    modalComponent.status = currentCashRegister.clientStatus;
    modalComponent.networkStatus = currentCashRegister.clientNetworkStatus;
    modalComponent.connectedCode = currentCashRegister.cfsCode;
    modalComponent.cashregister = currentCashRegister;
    modalComponent.cashregisterId = currentCashRegister.id;

    modal.closed
      .pipe(
        filter((result: CFSConnectionModalResult) => result?.closingReason === "confirm"),
        delay(this.auditTimeDelay),
        take(1)
      )
      .subscribe((result: CFSConnectionModalResult) => {
        const locationId: number = this.locationService.getSelectedLocationLocalStorage();
        const { oldCode, updatedCashRegister } = result;
        const { cfsCode, id } = updatedCashRegister;

        if (cfsCode && oldCode && cfsCode !== oldCode) {
          this.cashRegistersService.clearCashRegisterSubscription(cfsCode, id);
        }

        this.cashRegistersService.update(locationId);
      });
  }

  updateCFSCarousel(): void {
    const currentCashRegister: ClientCashRegister = this.cashRegistersService.getCurrentCashRegister();

    if (currentCashRegister.cfsCode) {
      const settings: CFSConnectionSettings = this.cfsConnectionService.getCFSConnectionSettings(currentCashRegister.id);

      this.cfsConnectionService.publishUpdateCarousel(settings, currentCashRegister.cfsCode);
    }
  }

  goToProfile(): void {
    window.location.href = this.profileUrl;
  }

  navigateToHome(): void {
    this.configurationService
      .navigateHomeUrl()
      .pipe(take(1), alertApiError(this.toaster))
      .subscribe((url) => {
        window.location.href = url;
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();

    this.cfsConnectionService.destroySubscriptions();
  }

  private setCurrentCashRegister(): void {
    this.cashRegistersService.init(this.accountService.userId);

    this.cashRegistersService.runStateBroadcast().pipe(takeUntil(this.unsubscribe$)).subscribe();
    this.cashRegistersService.runUpdateCashregisters().pipe(takeUntil(this.unsubscribe$)).subscribe();

    this.cashRegistersService.currentCashRegister$.pipe(takeUntil(this.unsubscribe$)).subscribe();
  }

  private onEvents(): void {
    this.route.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(() => {
      this.loginOrLandingOnly = location.pathname.startsWith("/landing"); // todo: check if it's needed
    });

    combineLatest([
      this.accountService.userChanged$.pipe(filter((v) => !!v)),
      this.companyDataService.subscriptionChanged$.pipe(filter((v) => !!v))
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([user, subscription]) => {
        this.isMinLocationManager = this.authService.requiredMinimumRole(RoleTypes.LocationManager);
        this.user = user;
        this.periodEndDate = this.getPeriodEndDate(subscription.endDate);
        this.periodEndDays = this.getPeriodEndDays(subscription.endDate);
        this.brandLogo$.next(user?.logo || "");
        this.cashRegistersService.init(this.accountService.userId);
        this.hasCfsTerminals = this.permissionService.hasPermission(PermissionTypeDto.CfsTerminals);
        this.hasLogoutButton = this.permissionService.hasPermission(PermissionTypeDto.Logout);
        this.subscription$.next(subscription);
      });
  }

  private setWindowWidth(): void {
    this.windowWidth = window.innerWidth;
  }

  private setConfigData(): void {
    const appData: IApplicationConfig = this.configSvc.appData;
    const { identityUrl, authConfig, adminUIUrl } = appData;
    const returnUrl = authConfig.redirectUrl;
    this.profileUrl = `${identityUrl}?returnUrl=${returnUrl}`;
    this.adminPortalUrl = adminUIUrl;
  }

  private setIsMobileApp(): void {
    const mobileAppUserAgent = "CMC Mobile App";
    this.isMobileApp = navigator.userAgent === mobileAppUserAgent;
  }

  private getPeriodEndDate(date: Date): string {
    const endDate = new Date(date);
    const dd = String(endDate.getDate()).padStart(2, "0");
    const mm = String(endDate.getMonth() + 1).padStart(2, "0");
    const yyyy = endDate.getFullYear();

    return mm + "/" + dd + "/" + yyyy;
  }

  private getPeriodEndDays(date: Date): number {
    const endDate = new Date(date);
    const today = new Date();
    const difference = endDate.getTime() - today.getTime();

    return Math.ceil(difference / (1000 * 3600 * 24));
  }
}
